// Supabase-only AuthContext (migrated from legacy Firebase version)
import React from 'react';
import supabase, { SUPABASE_ENABLED } from '../lib/supabaseClient';

const AuthContext = React.createContext(null);

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  const [initializing, setInitializing] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Load initial session & subscribe to changes
  React.useEffect(() => {
    let active = true;
    async function init() {
      if (!SUPABASE_ENABLED) {
        // No Supabase in this build: expose unauthenticated state without subscribing
        setUser(null);
        setInitializing(false);
        return;
      }
      try {
        const { data } = await supabase.auth.getSession();
        if (!active) return;
        setUser(data?.session?.user || null);
      } catch (e) {
        setError(e.message);
      } finally {
        setInitializing(false);
      }
    }
    init();
    // Subscribe only when Supabase is enabled
    let sub = null;
    if (SUPABASE_ENABLED) {
      const result = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user || null);
      });
      // SDK v2 returns { data: { subscription } }
      sub = result?.data?.subscription || result?.data || null;
    }
    return () => {
      active = false;
      if (sub && typeof sub.unsubscribe === 'function') sub.unsubscribe();
    };
  }, []);

  // Supabase sign in
  const signIn = React.useCallback(async ({ email, password }) => {
    setError(null);
    if (!SUPABASE_ENABLED) throw new Error('Supabase disabled');
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) { setError(err.message); throw err; }
  }, []);

  // Supabase sign up
  const signUp = React.useCallback(async ({ email, password }) => {
    setError(null);
    if (!SUPABASE_ENABLED) throw new Error('Supabase disabled');
    const { error: err } = await supabase.auth.signUp({ email, password });
    if (err) { setError(err.message); throw err; }
  }, []);

  const signOut = React.useCallback(async () => {
    setError(null);
    if (!SUPABASE_ENABLED) { setUser(null); return; }
    const { error: err } = await supabase.auth.signOut();
    if (err) { setError(err.message); throw err; }
  }, []);

  // Provide a fetch wrapper similar to legacy authenticatedFetch (uses Supabase JWT)
  const authenticatedFetch = React.useCallback(async (url, options = {}) => {
    if (!user) throw new Error('Not authenticated');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) throw new Error('Session expired');
    const isAbsolute = /^https?:\/\//i.test(url);
    // Support both env variable names for backend URL
    let base = (process.env.REACT_APP_BACKEND_BASE_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
    // Default to same-origin when no explicit backend base is provided.
    // This avoids connection-refused noise in dev when a backend is not running.
    if (!base && typeof window !== 'undefined') {
      base = window.location?.origin || '';
    }
    const resolvedUrl = isAbsolute ? url : `${base}${url.startsWith('/') ? url : '/' + url}`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {})
    };
    const doFetch = async (targetUrl) => fetch(targetUrl, { ...options, headers });
    let res;
    try {
      res = await doFetch(resolvedUrl);
    } catch (e) {
      // Network error: optionally retry against common local backends for API endpoints when no explicit backend is configured
      const noExplicitBase = !(process.env.REACT_APP_BACKEND_BASE_URL || process.env.REACT_APP_API_BASE_URL || '').trim();
      const isApiPath = typeof url === 'string' && (/^\/?api\//.test(url.replace(/^\//, '')) || url.startsWith('/arrange'));
      if (!isAbsolute && noExplicitBase && isApiPath) {
        const candidates = [
          `http://localhost:3001${url.startsWith('/') ? url : '/' + url}`,
          `http://localhost:8000${url.startsWith('/') ? url : '/' + url}`,
        ];
        let lastErr = e;
        for (const fallback of candidates) {
          try {
            res = await doFetch(fallback);
            break;
          } catch (e2) {
            lastErr = e2;
          }
        }
        if (!res) throw lastErr;
      } else {
        throw e;
      }
    }

    // If same-origin returns 404 for API path and no explicit base is set, retry once against localhost:8000
    if (res.status === 404) {
      const noExplicitBase = !(process.env.REACT_APP_BACKEND_BASE_URL || process.env.REACT_APP_API_BASE_URL || '').trim();
      const isApiPath = typeof url === 'string' && (/^\/?api\//.test(url.replace(/^\//, '')) || url.startsWith('/arrange'));
      if (!isAbsolute && noExplicitBase && isApiPath) {
        const candidates = [
          `http://localhost:3001${url.startsWith('/') ? url : '/' + url}`,
          `http://localhost:8000${url.startsWith('/') ? url : '/' + url}`,
        ];
        for (const fallback of candidates) {
          try {
            const retry = await doFetch(fallback);
            if (retry.ok || retry.status !== 404) {
              res = retry;
              break;
            }
          } catch (_) {
            // continue
          }
        }
      }
    }
    if (res.status === 401) {
      await signOut();
      throw new Error('Session expired');
    }
    return res;
  }, [user, signOut]);

  // Backward compatibility layer for legacy consumers
  const value = {
    user,
    initializing,
    loading: initializing, // alias for old code
    error,
    // New API
    signIn,
    signUp,
    signOut,
    // Legacy naming expected by some components
    login: async (email, password) => signIn({ email, password }),
    register: async (email, password) => signUp({ email, password }),
    logout: signOut,
    authenticatedFetch,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
